import { useMemo, useState, useEffect, useCallback, useRef, useContext } from 'react';
import isEmpty from 'lodash/isEmpty';
import { DictionariesContext } from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import { useHeaderMenuContext as useHeaderMenuContextV2 } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import { selectDefaultRegionId } from '@/creditflowresearch/domain/Region/selectors/selectDefaultRegionId';
import { selectAccessibleRegionId } from '@/creditflowresearch/domain/Region/selectors/selectAccessibleRegionId';
import type { IRegionAccessControl } from '@/creditflowresearch/domain/Region/selectors/hasRegionAccess';

export interface IUseRegionStateResult {
  selectedRegionId: string,
  defaultRegionId: string | null,
  accessControl: IRegionAccessControl,
  setSelectedRegionId: (regionId: string) => void,
  regionsList: IRegion[],
}

export const useRegionState = (preferredRegionIds?: string[]): IUseRegionStateResult => {
  const legacy = useContext(DictionariesContext);
  const { regions: v2Regions } = useHeaderMenuContextV2();
  const regionsList = legacy?.regionsList ?? v2Regions;
  const { regions: accessibleRegionsIds, plan, preferences } = useUserPlanContext();

  const accessControl = useMemo<IRegionAccessControl>(() => ({
    plan,
    accessibleRegionsIds,
  }), [plan, accessibleRegionsIds]);

  const preferredIds = preferredRegionIds ?? preferences?.regionIds;

  const defaultRegionId = useMemo(() => {
    if (isEmpty(regionsList)) {
      return null;
    }
    return selectDefaultRegionId(regionsList, accessControl, preferredIds);
  }, [regionsList, accessControl, preferredIds]);

  const [selectedRegionId, setSelectedRegionIdState] = useState<string>(() => {
    if (isEmpty(regionsList)) {
      return selectDefaultRegionId([], accessControl, preferredIds);
    }
    return selectDefaultRegionId(regionsList, accessControl, preferredIds);
  });

  const selectedRegionIdRef = useRef(selectedRegionId);
  selectedRegionIdRef.current = selectedRegionId;

  const hasUserInteractedRef = useRef(false);

  useEffect(() => {
    if (isEmpty(regionsList) || !defaultRegionId) {
      return;
    }

    const accessibleRegionId = selectAccessibleRegionId(
      selectedRegionIdRef.current,
      regionsList,
      accessControl,
      preferredIds,
    );

    if (accessibleRegionId !== selectedRegionIdRef.current) {
      setSelectedRegionIdState(accessibleRegionId);
      return;
    }

    if (!hasUserInteractedRef.current && defaultRegionId !== selectedRegionIdRef.current) {
      setSelectedRegionIdState(defaultRegionId);
    }
  }, [regionsList, accessControl, preferredIds, defaultRegionId]);

  const setSelectedRegionId = useCallback((regionId: string) => {
    hasUserInteractedRef.current = true;
    setSelectedRegionIdState(regionId);
  }, []);

  return {
    selectedRegionId,
    defaultRegionId,
    accessControl,
    setSelectedRegionId,
    regionsList,
  };
};
